import React from "react";
import { Box } from "@mui/material";
import { Spinner } from "components/Spinner";
import queryString from 'query-string'
import utils from '../../libs/utils'
const parsedQuery = queryString.parse(window.location.search);
const clientID = parsedQuery.clientId as string

const buildTransactionRequestTypes = ['TRANSFER', 'GAS_TRANSFER', 'TOKEN_TRANSFER', 'NFT_TRANSFER', 'CONTRACT_EXECUTION'];
const buildSignatureRequestTypes = ['MESSAGE', 'EIP712'];

const transactionRequestTypes = [
  'AE_TRANSACTION',
  'BSC_CONTRACT_EXECUTION',
  'BSC_BEP20_TRANSACTION',
  'BSC_ERC721_TRANSACTION',
  'BSC_TRANSACTION',
  'AVAC_CONTRACT_EXECUTION',
  'AVAC_BEP20_TRANSACTION',
  'AVAC_ERC721_TRANSACTION',
  'AVAC_TRANSACTION',
  'BTC_TRANSACTION',
  'ETH_TRANSACTION',
  'ETHEREUM_CONTRACT_EXECUTION',
  'ETHEREUM_ERC20_TRANSACTION',
  'ETHEREUM_ERC721_TRANSACTION',
  'GO_TRANSACTION',
  'GOCHAIN_GO20_TRANSACTION',
  'LTC_TRANSACTION',
  'MATIC_CONTRACT_EXECUTION',
  'MATIC_ERC20_TRANSACTION',
  'MATIC_ERC721_TRANSACTION',
  'MATIC_TRANSACTION',
  'NEO_CONTRACT_EXECUTION',
  'NEO_GAS_TRANSACTION',
  'NEO_NATIVE_TRANSACTION',
  'TRC10_TRANSACTION',
  'TRON_CONTRACT_EXECUTION',
  'TRX_TRANSACTION',
  'VECHAIN_CONTRACT_EXECUTION',
  'VECHAIN_ERC1155_TRANSACTION',
  'VECHAIN_VIP180_TRANSACTION',
  'VET_TRANSACTION',
  'VTHO_TRANSACTION'];

const signatureRequestTypes = [
  'AETERNITY_RAW',
  'AETERNITY_TRANSACTION',
  'AVAC_RAW',
  'AVAC_TRANSACTION',
  'AVAC_EIP712',
  'BITCOIN_TRANSACTION',
  'BSC_RAW',
  'BSC_TRANSACTION',
  'ETHEREUM_RAW',
  'BSC_EIP712',
  'ETHEREUM_EIP712',
  'ETHEREUM_TRANSACTION',
  'GOCHAIN_RAW',
  'GOCHAIN_TRANSACTION',
  'LITECOIN_TRANSACTION',
  'MATIC_RAW',
  'MATIC_TRANSACTION',
  'NEO_CONTRACT_EXECUTION',
  'NEO_GAS_TRANSACTION',
  'NEO_MESSAGE',
  'NEO_NATIVE_TRANSACTION',
  'TRC10_TRANSACTION',
  'TRON_RAW',
  'TRON_TRANSACTION',
  'VECHAIN_TRANSACTION',
  'VECHAIN_RAW'];

function isSignRequest(action: string) {
  return action === 'sign';
}

function isResubmitRequest(action: string) {
  return action === 'resubmit';
}

function isCancelRequest(action: string) {
  return action === 'cancel';
}

function isGenericTransactionRequest(requestData: any) {
  return buildTransactionRequestTypes.includes(requestData.type);
}

function isGenericSignatureRequest(requestData: any) {
  return buildSignatureRequestTypes.includes(requestData.type);
}

function isNativeTransactionRequest(requestData: any) {
  return transactionRequestTypes.includes(requestData.type);
}

function isNativeSignatureRequest(requestData: any) {
  return signatureRequestTypes.includes(requestData.type);
}

function isSavedTransactionRequest(requestData: any) {
  return !!requestData.transactionId;
}

function isConfirmationRequest(requestData: any) {
  return !!requestData.confirmationRequestType;
}

function buildRedirectUri() {
  return new URL(process.env.REACT_APP_CONNECT + '/popup/transaction/finish.html', window.location.href).toString();
}

function gotoResubmitRequest(requestData: any, correlationID: string) {
  const url = '/transaction/resubmit/' + requestData.transactionId;
  return utils.buildUrl(url, { redirectUri: buildRedirectUri(), correlationID: correlationID });
}

function gotoCancelRequest(requestData: any, correlationID: string) {
  const url = '/transaction/cancel/' + requestData.transactionId;
  return utils.buildUrl(url, { redirectUri: buildRedirectUri(), correlationID: correlationID });
}

function gotoSignRequest(requestData: any, correlationID: string) {
  let url = '';
  if (isGenericSignatureRequest(requestData)) {
    url = '/transaction/sign';
  } else if (isNativeSignatureRequest(requestData)) {
    url = '/transaction/sign/' + requestData.type;
  } else {
    // Generic
    url = '/transaction/sign';
  }
  return utils.buildUrl(url, { redirectUri: buildRedirectUri(), correlationID: correlationID });
}

function gotoExecuteRequest(requestData: any, correlationID: string) {
  let url = '';
  if (isGenericTransactionRequest(requestData)) {
    url = '/transaction/execute';
  } else if (isNativeTransactionRequest(requestData)) {
    url = '/transaction/execute/' + requestData.type;
  } else if (isSavedTransactionRequest(requestData)) {
    url = '/transaction/execute/' + requestData.transactionId;
  } else if (isConfirmationRequest(requestData)) {
    url = '/confirm/' + requestData.confirmationRequestType.toLowerCase();
  } else {
    // Generic TransactionRequest
    url = '/transaction/execute';
  }
  return utils.buildUrl(url, { redirectUri: buildRedirectUri(), correlationID: correlationID });
}

function getAction(requestData: any, action: string) {
  const correlationID = parsedQuery.cid as string;
  if (isSignRequest(action)) {
    return gotoSignRequest(requestData, correlationID);
  } else if (isResubmitRequest(action)) {
    return gotoResubmitRequest(requestData, correlationID);
  } else if (isCancelRequest(action)) {
    return gotoCancelRequest(requestData, correlationID);
  } else {
    return gotoExecuteRequest(requestData, correlationID);
  }
}

function onMessage(event: any) {
  if (event && event.data) {
    if (event.data.type === 'SEND_TRANSACTION_DATA') {
      const params = Object.assign({}, event.data.params);
      const action = getAction(params.transactionRequest, params.action);

      if (action !== 'invalid') {
        const form = document.getElementById('redirectForm') as HTMLFormElement;
        form.method = 'POST';
        form.action = process.env.REACT_APP_API + action;
        utils.addHiddenInput(form, 'clientid', clientID);
        utils.addHiddenInput(form, 'bearerToken', params.bearerToken);
        utils.addHiddenInput(form, 'data', JSON.stringify(Object.assign({}, params.transactionRequest)));
        form.submit();
      } else {
        window.location.href = utils.addRequestParams(buildRedirectUri(), {
          status: 'FAILED',
          error: 'Invalid action: "' + params.action + '"'
        });
      }
    }
  }
}

window.addEventListener('message', onMessage.bind(this));
window?.opener?.postMessage({ type: 'POPUP_MOUNTED', correlationID: parsedQuery.cid }, '*');
export default function PopupTransactionInit() {
  return (
    <Box p={10} display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="100vh">
      <form id="redirectForm">
      </form>
      <Spinner color="black" />
    </Box>
  );
}